import React, { useRef } from 'react';
import { JcCommonProps, JcUtils } from '../utils/JcUtils';
import { useJcScrollFade } from './JcScrollFade';

export interface JcScrollListProps extends JcCommonProps {
    readonly children: React.ReactNode;
    /** Accessible name for the list, announced by assistive tech (e.g. "Schedule game days"). */
    readonly label?: string;
    /** Scroll axis and fade edges. Defaults to `'horizontal'`. */
    readonly direction?: 'horizontal' | 'vertical';
}

/**
 * A scrollable list of items (horizontal by default, or vertical via
 * `direction`). Renders a semantic `<ul>`/`<li>` so assistive tech announces
 * "list, N items", and shows a soft gradient fade at whichever edge still has
 * content scrolled out of view — hinting that the row scrolls and there is
 * more to see (the shared `JcScrollFade` treatment). The fade color defaults
 * to the app background; override `--jc-scroll-fade-color` to match a tinted
 * surface.
 */
export const JcScrollList = (props: JcScrollListProps): React.ReactElement => {
    const { direction = 'horizontal' } = props;
    const vertical = direction === 'vertical';
    const commonProps = JcUtils.commonProps(props, [
        'jc-scroll-list',
        'jc-scroll-fade',
        vertical && 'jc-scroll-list-vertical',
        vertical && 'jc-scroll-fade-vertical',
    ]);
    const trackRef = useRef<HTMLUListElement>(null);
    const { fadeStart, fadeEnd } = useJcScrollFade(trackRef, vertical);

    const className = JcUtils.classNameString([
        commonProps.className,
        fadeStart && 'jc-scroll-fade-start',
        fadeEnd && 'jc-scroll-fade-end',
    ]);

    const items = React.Children.toArray(props.children);

    return (
        <div {...commonProps} className={className}>
            <ul className="jc-scroll-list-track" ref={trackRef} aria-label={props.label}>
                {items.map((child, index) => (
                    <li
                        key={React.isValidElement(child) && child.key != null ? child.key : index}
                        className="jc-scroll-list-item"
                    >
                        {child}
                    </li>
                ))}
            </ul>
        </div>
    );
};
